import {PLANET_HD_V1_ENABLED} from '../../../../../../config/featureFlags'
import * as LandService from '../../../services/LandService'
import {
  getApiLayerForImageType,
  getRemoteSensingParams,
  isHdImageType,
} from './imageTypeUtils'
import {buildRemoteSensingCacheKey} from './remoteSensingCacheKey'
import {normalizeRemoteSensingSource} from './remoteSensingModel'

const hashGeometry = (geometry) => {
  if (geometry == null) {
    return ''
  }

  const serialized = JSON.stringify(geometry)
  let hash = 2166136261

  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }

  return `client-${(hash >>> 0).toString(16)}`
}

const getGeometryHash = (acquisition, land) => (
  acquisition?.geometryHash ||
  land?.geometry_hash ||
  land?.geometryHash ||
  hashGeometry(land?.polygons || land?.polygon)
)

export const isCanonicalPlanetHdLayerRequest = ({acquisition, imageType}) => (
  PLANET_HD_V1_ENABLED &&
  isHdImageType(imageType) &&
  Boolean(acquisition?.acquisitionId)
)

export const buildImageLayerCacheOptions = ({
  acquisition,
  algorithmVersion = '',
  date,
  enhanced = false,
  imageType,
  land,
}) => ({
  accessId: acquisition?.accessId || '',
  companyPeriodId: acquisition?.companyPeriodId || '',
  algorithmVersion: acquisition?.algorithmVersion || algorithmVersion,
  date,
  enhanced,
  geometryHash: getGeometryHash(acquisition, land),
  imageType,
  sceneId: acquisition?.acquisitionId || acquisition?.itemId || '',
  // The catalog acquisition describes the scene, not the currently selected
  // visualization. Its source usually says "visible"; using that unchanged
  // would make Visible, NDVI and EVI share one cached raster/request key.
  source: normalizeRemoteSensingSource({
    ...(acquisition?.source || {}),
    layer: getApiLayerForImageType(imageType),
  }, imageType),
})

export const buildImageLayerRequestKey = ({landId, ...cacheOptions}) => (
  buildRemoteSensingCacheKey({
    ...cacheOptions,
    landId,
  })
)

export const requestRemoteSensingLayer = ({
  acquisition,
  cancelTokenRequest,
  imageType,
  land,
  layer,
  options,
}) => {
  if (isHdImageType(imageType)) {
    if (isCanonicalPlanetHdLayerRequest({acquisition, imageType})) {
      return LandService.getHdAcquisitionLayer(
        land.land_id,
        acquisition.acquisitionId,
        layer,
        {enhanced: Boolean(options?.enhanced)},
        cancelTokenRequest
      )
    }

    return Promise.resolve({
      ok: false,
      data: null,
      error_code: 'planet_hd_acquisition_required',
    })
  }

  return LandService.getLayerV2(
    land.land_id,
    layer,
    getRemoteSensingParams(imageType, options),
    cancelTokenRequest
  )
}

export const requestRemoteSensingTiff = ({
  acquisition,
  cancelTokenRequest,
  imageType,
  land,
  layer,
  options,
}) => {
  if (isHdImageType(imageType)) {
    // Planet analytical rasters remain server-side. This also prevents an HD
    // selection without canonical acquisition lineage from falling back to
    // the legacy Sentinel TIFF endpoint.
    return Promise.resolve({ok: false, data: null, headers: {}})
  }

  return LandService.getLayerTiff(
    land.land_id,
    layer,
    getRemoteSensingParams(imageType, options),
    cancelTokenRequest
  )
}
